import * as React from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import { Header } from '@/components/layout/header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  ArrowLeft,
  Loader2,
  Plus,
  Filter,
  UserPlus,
  Users,
  AlertCircle,
} from 'lucide-react'
import { AudienceSelector } from '@/components/whatsapp/audience-selector'
import { ContactPicker } from '@/components/whatsapp/contact-picker'
import { useCreateWAAudience } from '@/hooks/useWAAudiences'
import type { WAAudienceType, WAAudienceFilter, WAAudienceContactSearchResult } from '@/lib/api-client'

export const Route = createFileRoute('/whatsapp-audiences_/new')({
  component: NewAudiencePage,
})

function NewAudiencePage() {
  const navigate = useNavigate()
  const createMutation = useCreateWAAudience()

  // Form state
  const [name, setName] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [type, setType] = React.useState<WAAudienceType>('dynamic')
  const [filters, setFilters] = React.useState<WAAudienceFilter>({})
  const [selectedContacts, setSelectedContacts] = React.useState<WAAudienceContactSearchResult[]>([])
  const [error, setError] = React.useState<string | null>(null)

  const handleSubmit = () => {
    setError(null)

    if (!name.trim()) {
      setError('Audience name is required')
      return
    }

    if (type === 'manual' && selectedContacts.length === 0) {
      setError('Select at least one contact for manual audience')
      return
    }

    createMutation.mutate(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        type,
        filters: type === 'dynamic' ? filters : undefined,
        contactIds: type === 'manual' ? selectedContacts.map(c => c.contactId) : undefined,
      },
      {
        onSuccess: () => {
          navigate({ to: '/whatsapp-audiences' })
        },
        onError: (err) => {
          setError(err.message)
        },
      }
    )
  }

  return (
    <div className="flex flex-col h-full">
      <Header
        title="Create Audience"
        subtitle="Build a reusable audience segment for your campaigns"
      />

      <div className="flex-1 overflow-auto p-3 sm:p-4 lg:p-6">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          className="mb-4 -ml-2 text-text-muted hover:text-text-dark"
          onClick={() => navigate({ to: '/whatsapp-audiences' })}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Audiences
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left: Form */}
          <div className="lg:col-span-3 space-y-4">
            {/* Name & Description */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div>
                <Label htmlFor="aud-name" className="text-sm font-medium mb-2 block">
                  Audience Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="aud-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. VIP Patients, Inactive 30d+"
                  className="h-10"
                />
              </div>
              <div>
                <Label htmlFor="aud-desc" className="text-sm font-medium mb-2 block">
                  Description
                </Label>
                <Textarea
                  id="aud-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description about this audience..."
                  className="min-h-[100px]"
                />
              </div>
            </div>

            {/* Type selector */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-3">
              <Label className="text-sm font-medium mb-2 block">Audience Type</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border text-left transition-colors',
                    type === 'dynamic'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/30'
                  )}
                  onClick={() => setType('dynamic')}
                >
                  <Filter className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Dynamic</p>
                    <p className="text-xs text-text-muted mt-0.5">
                      Filter-based. Re-evaluated at send time.
                    </p>
                  </div>
                </button>
                <button
                  type="button"
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border text-left transition-colors',
                    type === 'manual'
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/30'
                  )}
                  onClick={() => setType('manual')}
                >
                  <UserPlus className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Manual</p>
                    <p className="text-xs text-text-muted mt-0.5">
                      Hand-pick specific contacts.
                    </p>
                  </div>
                </button>
              </div>
            </div>

            {/* Dynamic: filter builder */}
            {type === 'dynamic' && (
              <div className="rounded-xl border border-border bg-card p-6">
                <AudienceSelector
                  value={filters}
                  onChange={setFilters}
                />
              </div>
            )}

            {/* Manual: contact picker */}
            {type === 'manual' && (
              <div className="rounded-xl border border-border bg-card p-6">
                <Label className="text-sm font-medium mb-3 block">Select Contacts</Label>
                <ContactPicker
                  selectedContacts={selectedContacts}
                  onContactsChange={setSelectedContacts}
                />
              </div>
            )}
          </div>

          {/* Right: Audience progress summary */}
          <div className="lg:col-span-2 lg:sticky lg:top-6 self-start">
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h3 className="text-sm font-medium text-text-dark">Audience Summary</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-text-muted">Audience Name</p>
                  <p className="text-sm font-medium text-text-dark truncate">
                    {name.trim() || 'Not set'}
                  </p>
                </div>
                {description.trim() && (
                  <div>
                    <p className="text-xs text-text-muted">Description</p>
                    <p className="text-sm text-text-dark">{description}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-text-muted">Type</p>
                  <p className="text-sm font-medium text-text-dark">
                    {type === 'dynamic' ? 'Dynamic' : 'Manual'}
                  </p>
                </div>
              </div>

              <div className="border-t border-border/50 pt-4">
                {type === 'dynamic' && (
                  <div className="space-y-2">
                    <p className="text-xs text-text-muted">Active Filters</p>
                    {Object.keys(filters).length === 0 ? (
                      <p className="text-sm text-text-muted">No filters set — all contacts will match</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {filters.branchIds && filters.branchIds.length > 0 && (
                          <Badge variant="outline" className="text-[10px]">
                            {filters.branchIds.length} branch{filters.branchIds.length > 1 ? 'es' : ''}
                          </Badge>
                        )}
                        {filters.inactiveDays && (
                          <Badge variant="outline" className="text-[10px]">
                            {filters.inactiveDays}+ days inactive
                          </Badge>
                        )}
                        {filters.maxInactiveDays && (
                          <Badge variant="outline" className="text-[10px]">
                            Max {filters.maxInactiveDays} days
                          </Badge>
                        )}
                        {filters.gender && (
                          <Badge variant="outline" className="text-[10px]">
                            {filters.gender}
                          </Badge>
                        )}
                        {filters.minTotalSpent && (
                          <Badge variant="outline" className="text-[10px]">
                            RM{filters.minTotalSpent}+ spent
                          </Badge>
                        )}
                        {filters.maxTotalSpent && (
                          <Badge variant="outline" className="text-[10px]">
                            Max RM{filters.maxTotalSpent}
                          </Badge>
                        )}
                        {filters.ageMin && (
                          <Badge variant="outline" className="text-[10px]">
                            Age {filters.ageMin}+
                          </Badge>
                        )}
                        {filters.ageMax && (
                          <Badge variant="outline" className="text-[10px]">
                            Max age {filters.ageMax}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {type === 'manual' && (
                  <div className="space-y-2">
                    <p className="text-xs text-text-muted">Selected Contacts</p>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
                      <Users className="w-5 h-5 text-primary" />
                      <span className="text-lg font-semibold text-text-dark">
                        {selectedContacts.length}
                      </span>
                      <span className="text-sm text-text-muted">
                        contact{selectedContacts.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {selectedContacts.length > 0 && (
                      <div className="space-y-1">
                        {selectedContacts.slice(0, 5).map((c) => (
                          <div
                            key={c.contactId}
                            className="flex items-center justify-between p-2 rounded-md bg-muted/30 text-xs"
                          >
                            <span className="font-medium text-text-dark">{c.patientName}</span>
                            <Badge variant="outline" className="text-[10px]">
                              {c.branchName}
                            </Badge>
                          </div>
                        ))}
                        {selectedContacts.length > 5 && (
                          <p className="text-[10px] text-text-muted text-center py-1">
                            +{selectedContacts.length - 5} more
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="max-w-2xl mx-auto mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <p className="text-xs text-destructive flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {error}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="max-w-full mt-6 flex items-center justify-end gap-2 pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate({ to: '/whatsapp-audiences' })}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={createMutation.isPending || !name.trim()}
          >
            {createMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Create Audience
          </Button>
        </div>
      </div>
    </div>
  )
}
